import { yupResolver } from '@hookform/resolvers/yup'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import type { InferType } from 'yup'
import { mixed, number, object, string } from 'yup'

import {
    useCreateServiceMutation,
    useGetCategoriesQuery,
    useGetServicesByIdQuery,
    useUpdateServiceMutation,
} from '../../services/tenant/webPage'
import { getMediaUrl, setApiError } from '../../utils/helpers'
import Button from '../common/Button'
import { ChangePhotoPart } from '../common/ChangePhotoPart'
import Input, { InputStyles } from '../common/Input'
import Modal from '../common/Modal'
import SelectBox from '../common/SelectBox'
import TextArea from '../common/TextArea'

import { optionsSepviseStatus } from '@/utils/selectsOptionsConst'

const testPrice = (value: string) => {
    return /^\d*([.,]?\d{0,2})?$/.test(value)
}

const schema = object({
    name: string().required('Service name is required'),
    price: string()
        .required('Price is required')
        .test('is-decimal', 'Price must be a decimal number', val => {
            return !isNaN(Number(val)) && testPrice(val ?? '')
        }),
    categoryId: number().required('Category is required'),
    description: string().required('Description is required'),
    status: string().required('Status is required'),
    image: mixed()
        .required('Image is required')
        .test('file-or-url', 'Неверный формат изображения', value => {
            // 👉 Разрешаем строку (например, URL)
            if (typeof value === 'string' || value instanceof File) {
                return true // уже загруженное изображение
            }
            return false
        }),
})

type FormType = InferType<typeof schema>

interface AddServiseModalProps {
    isOpen: boolean
    onClose: () => void
    id?: string | undefined | null
    setId?: React.Dispatch<React.SetStateAction<string | null>>
}

const AddServiseModal = ({
    isOpen,
    onClose,
    id,
    setId,
}: AddServiseModalProps) => {
    const { data } = useGetCategoriesQuery()
    const [createServise, { isLoading: createLoading }] =
        useCreateServiceMutation()
    const [updateService, { isLoading: updateLoading }] =
        useUpdateServiceMutation()
    const { data: serviceData, isLoading: serviceLoading } =
        useGetServicesByIdQuery(id as string, {
            skip: !id,
        })
    const categoryOption =
        data?.data?.map(category => ({
            value: category.id,
            label: category.name,
        })) || []

    const {
        register,
        handleSubmit,
        setValue,
        control,
        formState: { errors, isDirty },
        setError,
    } = useForm<FormType>({
        mode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            status: 'active',
            price: '0.00',
        },
    })

    useEffect(() => {
        if (serviceData && id) {
            setValue('name', serviceData.data.name)
            setValue('price', String(serviceData.data.price))
            setValue('description', serviceData.data.description)
            setValue('categoryId', serviceData.data.categoryId)
            setValue('status', serviceData.data?.status)
            setValue('image', serviceData.data?.thumbail)
        }
    }, [serviceData, id])

    const handelClose = () => {
        onClose()
        if (setId) setId(null)
    }

    const onSubmit = async (data: FormType) => {
        const formData = new FormData()
        Object.entries(data).forEach(([key, value]) => {
            if (key === 'image' && value instanceof File) {
                formData.append('thumbail', value, value.name)
            } else {
                formData.append(key, String(value))
            }
        })

        let res = null
        try {
            if (id) {
                res = await updateService({
                    serviceId: id,
                    body: formData,
                }).unwrap()
            } else {
                res = await createServise(formData).unwrap()
            }
            if (res?.success) {
                toast.success('Updated successfully!')
                handelClose()
            } else {
                toast.error(res?.message || 'Something went wrong')
                setApiError(res?.errors || [], setError)
            }
        } catch (error) {
            toast.error('Something went wrong')
            console.error('rejected', error)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handelClose}
            isLoading={serviceLoading}
            title={id ? 'Edit Service' : 'Create Service'}
        >
            <div className="modal__body p-0">
                <div className="custom-form">
                    <div className="custom-form-body">
                        <div className="row gutters-16">
                            <div className="col-12">
                                <Controller
                                    name="image"
                                    control={control}
                                    render={({ field }) => (
                                        <ChangePhotoPart
                                            type="photo-load-rectangle"
                                            onChange={val => {
                                                field.onChange(val)
                                                // setPhoto(val as File)
                                                // setPhotoTouch(true)
                                            }}
                                            error={errors.image?.message}
                                            userPhoto={
                                                typeof field?.value === 'string'
                                                    ? getMediaUrl(field?.value)
                                                    : null
                                            }
                                            accept="image/gif, image/jpeg, image/png, image/jpg, image/webp, image/svg+xml"
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-12">
                                <Input
                                    inputStyle={InputStyles.outline}
                                    label="Service Name"
                                    placeholder="Enter Name..."
                                    error={errors.name?.message}
                                    {...register('name')}
                                />
                            </div>
                            <div className="col-12">
                                <Controller
                                    name="categoryId"
                                    control={control}
                                    render={({ field }) => (
                                        <SelectBox
                                            {...field}
                                            wrapperClassName="input--lg select-secondary select-left-spase-0"
                                            label="Category"
                                            options={categoryOption}
                                            placeholder={'Category'}
                                            error={errors.categoryId?.message}
                                            onChange={option =>
                                                field.onChange(option?.value)
                                            }
                                            value={field.value}
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-12">
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field }) => (
                                        <SelectBox
                                            {...field}
                                            wrapperClassName="input--lg select-secondary select-left-spase-0"
                                            label="Status"
                                            options={optionsSepviseStatus}
                                            placeholder={'Status'}
                                            error={errors.status?.message}
                                            onChange={option =>
                                                field.onChange(option?.value)
                                            }
                                            value={field.value}
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-12">
                                <Controller
                                    name="price"
                                    control={control}
                                    render={({
                                        field: { onChange, onBlur, value, ref },
                                    }) => (
                                        <Input
                                            inputStyle={InputStyles.outline}
                                            label="Price"
                                            // inputMode="decimal"
                                            type="text"
                                            value={value}
                                            onChange={e => {
                                                const val = e.target.value

                                                // 👇 Регулярка: только числа с макс 2 знаками после точки
                                                if (
                                                    testPrice(val) ||
                                                    val === ''
                                                ) {
                                                    onChange(val)
                                                }
                                            }}
                                            onBlur={() => {
                                                if (value !== '') {
                                                    const number =
                                                        parseFloat(value)
                                                    if (!isNaN(number)) {
                                                        onChange(
                                                            number.toFixed(2),
                                                        ) // форматируем при выходе
                                                    }
                                                }
                                                onBlur()
                                            }}
                                            placeholder="Enter Price..."
                                            error={errors.price?.message}
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-12">
                                <TextArea
                                    inputStyle={InputStyles.outline}
                                    className="textarea input--lg input--outline w-full --none-resize"
                                    label="Description"
                                    placeholder="Enter Description..."
                                    error={errors.description?.message}
                                    {...register('description')}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="custom-form-footer justify-end">
                        <div className="flex-group flex-group--xs">
                            <Button
                                type="submit"
                                onClick={handleSubmit(onSubmit)}
                                size="lg"
                                variant="primary-gradient"
                                disabled={!isDirty}
                                loading={createLoading || updateLoading}
                            >
                                <span>
                                    {id ? 'Save Changes' : 'Create Service'}
                                </span>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default AddServiseModal
